/**
 * HFDS — Headzflex Design System
 * Component: HF-007 Purchase Engine
 * Version: 1.0.2
 *
 * Fixes:
 * - Correct initial variant detection.
 * - Supports products with one or multiple variants.
 * - Finds product form by ID instead of depending on a data attribute.
 * - Correctly enables Add to Bag when a variant is available.
 * - Removes dependence on temporary inline scripts.
 * - Uses Shopify bundled section rendering during cart/add.js.
 * - Returns Ella cart-drawer HTML from the same add-to-cart request.
 * - Eliminates stale secondary cart section requests.
 */

(() => {
  "use strict";

  const ROOT_SELECTOR = "[data-hf-purchase-engine]";
  const SUBMIT_SELECTOR = '[data-hf-action="add-to-cart"]';
  const QUANTITY_INPUT_SELECTOR = "[data-hf-quantity-input]";
  const QUANTITY_MINUS_SELECTOR = "[data-hf-quantity-minus]";
  const QUANTITY_PLUS_SELECTOR = "[data-hf-quantity-plus]";
  const PRICE_SELECTOR = "[data-hf-product-price]";
  const COMPARE_PRICE_SELECTOR = "[data-hf-compare-price]";
  const SALE_LABEL_SELECTOR = "[data-hf-sale-label]";
  const INVENTORY_SELECTOR = "[data-hf-inventory]";
  const INVENTORY_TEXT_SELECTOR = "[data-hf-inventory-text]";
  const MESSAGE_SELECTOR = "[data-hf-purchase-message]";
  const INITIAL_VARIANT_SELECTOR = "[data-hf-initial-variant-json]";
  const CONFIRMATION_SELECTOR = "[data-hf-cart-confirmation]";

  class HFPurchaseEngine {
    /**
     * @param {HTMLElement} root
     */
    constructor(root) {
      this.root = root;
      this.productId = String(root.dataset.productId || "");
      this.formId = String(root.dataset.productFormId || "");

      this.currency =
        root.dataset.currency ||
        window.Shopify?.currency?.active ||
        "USD";

      this.lowStockThreshold = Number(
        root.dataset.lowStockThreshold || 5
      );

      this.form = this.formId
        ? document.getElementById(this.formId)
        : null;

      this.submitButton = root.querySelector(SUBMIT_SELECTOR);

      this.quantityInput = root.querySelector(
        QUANTITY_INPUT_SELECTOR
      );

      this.quantityMinus = root.querySelector(
        QUANTITY_MINUS_SELECTOR
      );

      this.quantityPlus = root.querySelector(
        QUANTITY_PLUS_SELECTOR
      );

      this.priceElement = root.querySelector(PRICE_SELECTOR);

      this.comparePriceElement = root.querySelector(
        COMPARE_PRICE_SELECTOR
      );

      this.saleLabelElement = root.querySelector(
        SALE_LABEL_SELECTOR
      );

      this.inventoryElement = root.querySelector(
        INVENTORY_SELECTOR
      );

      this.inventoryTextElement = root.querySelector(
        INVENTORY_TEXT_SELECTOR
      );

      this.messageElement = root.querySelector(
        MESSAGE_SELECTOR
      );

      this.currentVariant = null;
      this.submitting = false;

      this.handleVariantChange =
        this.handleVariantChange.bind(this);

      this.handleSubmit = this.handleSubmit.bind(this);

      this.handleQuantityMinus =
        this.handleQuantityMinus.bind(this);

      this.handleQuantityPlus =
        this.handleQuantityPlus.bind(this);

      this.handleQuantityChange =
        this.handleQuantityChange.bind(this);

      this.initialize();
    }

    initialize() {
      if (!window.HFDS) {
        console.error(
          "[HFDS Purchase Engine] HFDS core is unavailable."
        );
        return;
      }

      if (!(this.form instanceof HTMLFormElement)) {
        console.error(
          `[HFDS Purchase Engine] Product form "${this.formId}" was not found.`
        );

        this.showMessage(
          "The product form could not be initialized.",
          "error"
        );

        return;
      }

      if (!(this.submitButton instanceof HTMLElement)) {
        console.error(
          "[HFDS Purchase Engine] Add to Bag button was not found."
        );

        return;
      }

      this.form.addEventListener(
        "submit",
        this.handleSubmit
      );

      this.quantityMinus?.addEventListener(
        "click",
        this.handleQuantityMinus
      );

      this.quantityPlus?.addEventListener(
        "click",
        this.handleQuantityPlus
      );

      this.quantityInput?.addEventListener(
        "change",
        this.handleQuantityChange
      );

      this.root.addEventListener(
        "hfds:variant:change",
        this.handleVariantChange
      );

      this.initializeVariant();
      this.setQuantity(this.getQuantity());

      this.root.dataset.hfInitialized = "true";
    }

    initializeVariant() {
      const variantPicker = this.root.querySelector(
        "[data-hf-variant-picker]"
      );

      const pickerVariant =
        variantPicker?.hfVariantPicker?.currentVariant;

      if (pickerVariant) {
        this.setVariant(pickerVariant);
        return;
      }

      const initialVariantScript = this.root.querySelector(
        INITIAL_VARIANT_SELECTOR
      );

      const initialVariant = window.HFDS.getJSONScript(
        initialVariantScript,
        null
      );

      if (initialVariant?.id) {
        this.setVariant(initialVariant);
        return;
      }

      console.error(
        "[HFDS Purchase Engine] Initial variant could not be resolved."
      );

      this.setVariant(null);
    }

    /**
     * @param {CustomEvent} event
     */
    handleVariantChange(event) {
      const detail = event.detail || {};

      if (
        detail.productId &&
        String(detail.productId) !== this.productId
      ) {
        return;
      }

      this.setVariant(detail.variant || null);
    }

    /**
     * @param {Object|null} variant
     */
    setVariant(variant) {
      this.currentVariant = variant;

      this.updateVariantInput(variant);
      this.updatePrice(variant);
      this.updateInventory(variant);
      this.updateSubmitButton(variant);
      this.clearMessage();
    }

    /**
     * Synchronizes Shopify's variant ID field.
     *
     * @param {Object|null} variant
     */
    updateVariantInput(variant) {
      if (!(this.form instanceof HTMLFormElement)) {
        return;
      }

      let input = this.form.querySelector(
        'input[name="id"]'
      );

      if (!(input instanceof HTMLInputElement)) {
        input = document.createElement("input");
        input.type = "hidden";
        input.name = "id";
        this.form.appendChild(input);
      }

      input.value = variant?.id
        ? String(variant.id)
        : "";
    }

    /**
     * @param {Object|null} variant
     */
    updatePrice(variant) {
      if (!variant) {
        if (this.priceElement) {
          this.priceElement.textContent = "—";
        }

        this.hideComparePrice();
        this.hideSaleLabel();
        return;
      }

      if (this.priceElement) {
        this.priceElement.textContent =
          window.HFDS.formatMoney(
            Number(variant.price),
            this.currency
          );
      }

      const comparePrice = Number(
        variant.compare_at_price || 0
      );

      const price = Number(variant.price || 0);

      const hasDiscount =
        comparePrice > 0 &&
        comparePrice > price;

      if (hasDiscount && this.comparePriceElement) {
        this.comparePriceElement.textContent =
          window.HFDS.formatMoney(
            comparePrice,
            this.currency
          );

        this.comparePriceElement.hidden = false;
      } else {
        this.hideComparePrice();
      }

      if (hasDiscount && this.saleLabelElement) {
        const percentage = Math.round(
          ((comparePrice - price) / comparePrice) * 100
        );

        this.saleLabelElement.textContent =
          `Save ${percentage}%`;

        this.saleLabelElement.hidden = false;
      } else {
        this.hideSaleLabel();
      }
    }

    hideComparePrice() {
      if (!this.comparePriceElement) {
        return;
      }

      this.comparePriceElement.hidden = true;
      this.comparePriceElement.textContent = "";
    }

    hideSaleLabel() {
      if (!this.saleLabelElement) {
        return;
      }

      this.saleLabelElement.hidden = true;
      this.saleLabelElement.textContent = "";
    }

    /**
     * @param {Object|null} variant
     */
    updateInventory(variant) {
      if (
        !this.inventoryElement ||
        !this.inventoryTextElement
      ) {
        return;
      }

      this.inventoryElement.classList.remove(
        "is-in-stock",
        "is-low-stock",
        "is-unavailable"
      );

      if (!variant || variant.available !== true) {
        this.inventoryElement.classList.add(
          "is-unavailable"
        );

        this.inventoryTextElement.textContent =
          "Currently unavailable";

        return;
      }

      const inventoryQuantity = Number(
        variant.inventory_quantity
      );

      const tracksInventory =
        variant.inventory_management != null;

      if (
        tracksInventory &&
        Number.isFinite(inventoryQuantity) &&
        inventoryQuantity > 0 &&
        inventoryQuantity <= this.lowStockThreshold
      ) {
        this.inventoryElement.classList.add(
          "is-low-stock"
        );

        this.inventoryTextElement.textContent =
          `Low stock — ${inventoryQuantity} remaining`;

        return;
      }

      this.inventoryElement.classList.add(
        "is-in-stock"
      );

      this.inventoryTextElement.textContent =
        "In stock and ready to order";
    }

    /**
     * @param {Object|null} variant
     */
    updateSubmitButton(variant) {
      if (!(this.submitButton instanceof HTMLElement)) {
        return;
      }

      const available =
        Boolean(variant) &&
        variant.available === true;

      this.submitButton.disabled = !available;

      this.submitButton.setAttribute(
        "aria-disabled",
        String(!available)
      );

      this.submitButton.classList.toggle(
        "is-disabled",
        !available
      );

      const label = this.submitButton.querySelector(
        ".hf-button__label"
      );

      if (!label) {
        return;
      }

      if (!available) {
        label.textContent = "Currently Unavailable";
        return;
      }

      label.textContent =
        `Add to Bag — ${window.HFDS.formatMoney(
          Number(variant.price),
          this.currency
        )}`;
    }

    handleQuantityMinus() {
      this.setQuantity(this.getQuantity() - 1);
    }

    handleQuantityPlus() {
      this.setQuantity(this.getQuantity() + 1);
    }

    handleQuantityChange() {
      this.setQuantity(this.getQuantity());
    }

    getQuantity() {
      if (!(this.quantityInput instanceof HTMLInputElement)) {
        return 1;
      }

      const parsed = Number.parseInt(
        this.quantityInput.value,
        10
      );

      return Number.isFinite(parsed) ? parsed : 1;
    }

    /**
     * @param {number} value
     */
    setQuantity(value) {
      if (!(this.quantityInput instanceof HTMLInputElement)) {
        return;
      }

      const minimum = Math.max(
        Number.parseInt(
          this.quantityInput.min || "1",
          10
        ) || 1,
        1
      );

      const parsedMaximum = Number.parseInt(
        this.quantityInput.max || "",
        10
      );

      const maximum = Number.isFinite(parsedMaximum)
        ? parsedMaximum
        : Number.POSITIVE_INFINITY;

      const requestedValue =
        Number.parseInt(String(value), 10) || minimum;

      const nextValue = Math.min(
        Math.max(requestedValue, minimum),
        maximum
      );

      this.quantityInput.value = String(nextValue);

      if (this.quantityMinus) {
        this.quantityMinus.disabled =
          nextValue <= minimum;
      }

      if (this.quantityPlus) {
        this.quantityPlus.disabled =
          Number.isFinite(maximum) &&
          nextValue >= maximum;
      }
    }

    /**
     * @param {SubmitEvent} event
     */
    async handleSubmit(event) {
      event.preventDefault();

      if (this.submitting) {
        return;
      }

      if (
        !this.currentVariant ||
        this.currentVariant.available !== true
      ) {
        this.showMessage(
          "Choose an available size before adding this jersey.",
          "error"
        );

        window.HFDS.announce(
          "Choose an available size before adding this jersey.",
          "assertive"
        );

        return;
      }

      this.setSubmitting(true);
      this.clearMessage();

      try {
        const formData = new FormData(this.form);

        formData.set(
          "id",
          String(this.currentVariant.id)
        );

        formData.set(
          "quantity",
          String(this.getQuantity())
        );

        const cartDrawer =
          document.querySelector("cart-drawer");

        const sectionIds =
          typeof cartDrawer?.getSectionsToRender === "function"
            ? cartDrawer
                .getSectionsToRender()
                .map((section) => section?.id)
                .filter(Boolean)
            : [
                "cart-drawer",
                "cart-icon-bubble"
              ];

        formData.set(
          "sections",
          sectionIds.join(",")
        );

        formData.set(
          "sections_url",
          window.location.pathname
        );

        const root =
          window.Shopify?.routes?.root || "/";

        const response = await fetch(
          `${root}cart/add.js`,
          {
            method: "POST",
            headers: {
              Accept: "application/json",
              "X-Requested-With": "XMLHttpRequest"
            },
            body: formData
          }
        );

        const responseData = await response.json();

        if (!response.ok) {
          throw new Error(
            responseData?.description ||
              responseData?.message ||
              "The product could not be added."
          );
        }

        const cart = await this.getCart();

        this.updateCartCounts(cart?.item_count);
        this.showSuccess(responseData);
        this.emitCartEvents(responseData, cart);
      } catch (error) {
        console.error(
          "[HFDS Purchase Engine] Add to cart failed:",
          error
        );

        const message =
          error instanceof Error
            ? error.message
            : "The product could not be added.";

        this.showMessage(message, "error");

        window.HFDS.announce(
          message,
          "assertive"
        );

        window.HFDS.emit(
          "cart:add-error",
          {
            productId: this.productId,
            variant: this.currentVariant,
            quantity: this.getQuantity(),
            error: message
          },
          this.root
        );
      } finally {
        this.setSubmitting(false);
      }
    }

    async getCart() {
      const root =
        window.Shopify?.routes?.root || "/";

      const response = await fetch(`${root}cart.js`, {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      });

      if (!response.ok) {
        return null;
      }

      return response.json();
    }

    /**
     * @param {number|string} itemCount
     */
    updateCartCounts(itemCount) {
      const parsedCount = Number(itemCount);

      if (!Number.isFinite(parsedCount)) {
        return;
      }

      const count = String(parsedCount);

      const selectors = [
        "[data-cart-count]",
        "[data-cart-item-count]",
        ".cart-count-bubble span",
        ".cart-icon-bubble .text",
        ".header__cart-count",
        ".cart-count"
      ];

      document
        .querySelectorAll(selectors.join(","))
        .forEach((element) => {
          element.textContent = count;
          element.hidden = false;
        });
    }

    /**
     * @param {Object} lineItem
     */
    showSuccess(lineItem) {
      this.showMessage(
        "Added to your bag.",
        "success"
      );

      const productTitle =
        lineItem.product_title ||
        this.root.dataset.productTitle ||
        "Product";

      window.HFDS.announce(
        `${productTitle} added to your bag.`,
        "polite"
      );

      HFCartConfirmation.show({
        title: productTitle,
        variantTitle:
          lineItem.variant_title &&
          lineItem.variant_title !== "Default Title"
            ? lineItem.variant_title
            : "",
        image:
          lineItem.featured_image?.url ||
          lineItem.image ||
          "",
        cartUrl:
          `${window.Shopify?.routes?.root || "/"}cart`
      });
    }

    /**
     * @param {Object} lineItem
     * @param {Object|null} cart
     */
    emitCartEvents(lineItem, cart) {
      const detail = {
        productId: this.productId,
        variant: this.currentVariant,
        quantity: this.getQuantity(),
        lineItem,
        cart,
        sections:
          lineItem?.sections &&
          typeof lineItem.sections === "object"
            ? lineItem.sections
            : null
      };

      window.HFDS.emit(
        "cart:item-added",
        detail,
        this.root
      );

      document.dispatchEvent(
        new CustomEvent(
          "hfds:cart:sections-ready",
          {
            bubbles: true,
            detail
          }
        )
      );
    }

    /**
     * @param {boolean} submitting
     */
    setSubmitting(submitting) {
      this.submitting = submitting;

      this.form?.setAttribute(
        "aria-busy",
        String(submitting)
      );

      if (!this.submitButton) {
        return;
      }

      this.submitButton.classList.toggle(
        "is-loading",
        submitting
      );

      this.submitButton.setAttribute(
        "aria-busy",
        String(submitting)
      );

      const unavailable =
        !this.currentVariant ||
        this.currentVariant.available !== true;

      this.submitButton.disabled =
        submitting || unavailable;
    }

    /**
     * @param {string} message
     * @param {"success"|"error"|"info"} type
     */
    showMessage(message, type = "info") {
      if (!this.messageElement) {
        return;
      }

      this.messageElement.classList.remove(
        "is-success",
        "is-error",
        "is-info"
      );

      this.messageElement.textContent = message;
      this.messageElement.classList.add(`is-${type}`);
    }

    clearMessage() {
      if (!this.messageElement) {
        return;
      }

      this.messageElement.textContent = "";

      this.messageElement.classList.remove(
        "is-success",
        "is-error",
        "is-info"
      );
    }

    destroy() {
      this.form?.removeEventListener(
        "submit",
        this.handleSubmit
      );

      this.quantityMinus?.removeEventListener(
        "click",
        this.handleQuantityMinus
      );

      this.quantityPlus?.removeEventListener(
        "click",
        this.handleQuantityPlus
      );

      this.quantityInput?.removeEventListener(
        "change",
        this.handleQuantityChange
      );

      this.root.removeEventListener(
        "hfds:variant:change",
        this.handleVariantChange
      );

      delete this.root.dataset.hfInitialized;
    }
  }

  const HFCartConfirmation = (() => {
    let element = null;
    let timeoutId = null;

    function create() {
      const wrapper = document.createElement("div");

      wrapper.className = "hf-cart-confirmation";
      wrapper.dataset.hfCartConfirmation = "";
      wrapper.setAttribute("role", "status");
      wrapper.setAttribute("aria-live", "polite");
      wrapper.setAttribute("aria-atomic", "true");

      wrapper.innerHTML = `
        <div class="hf-cart-confirmation__content">
          <img
            class="hf-cart-confirmation__image"
            data-hf-confirmation-image
            alt=""
            hidden
          >

          <div>
            <p
              class="hf-cart-confirmation__title"
              data-hf-confirmation-title
            ></p>

            <p
              class="hf-cart-confirmation__variant"
              data-hf-confirmation-variant
            ></p>
          </div>

          <button
            class="hf-cart-confirmation__close"
            type="button"
            data-hf-confirmation-close
            aria-label="Close cart confirmation"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="m6 6 12 12"></path>
              <path d="m18 6-12 12"></path>
            </svg>
          </button>
        </div>

        <div class="hf-cart-confirmation__actions">
          <button
            class="hf-button hf-button--secondary hf-button--medium"
            type="button"
            data-hf-confirmation-continue
          >
            <span class="hf-button__content">
              <span class="hf-button__label">
                Continue Shopping
              </span>
            </span>
          </button>

          <a
            class="hf-button hf-button--primary hf-button--medium"
            href="/cart"
            data-hf-confirmation-cart
          >
            <span class="hf-button__content">
              <span class="hf-button__label">
                View Bag
              </span>
            </span>
          </a>
        </div>
      `;

      wrapper
        .querySelector("[data-hf-confirmation-close]")
        ?.addEventListener("click", hide);

      wrapper
        .querySelector("[data-hf-confirmation-continue]")
        ?.addEventListener("click", hide);

      document.body.appendChild(wrapper);

      return wrapper;
    }

    function show({
      title,
      variantTitle,
      image,
      cartUrl
    }) {
      element =
        element ||
        document.querySelector(CONFIRMATION_SELECTOR) ||
        create();

      const titleElement = element.querySelector(
        "[data-hf-confirmation-title]"
      );

      const variantElement = element.querySelector(
        "[data-hf-confirmation-variant]"
      );

      const imageElement = element.querySelector(
        "[data-hf-confirmation-image]"
      );

      const cartLink = element.querySelector(
        "[data-hf-confirmation-cart]"
      );

      if (titleElement) {
        titleElement.textContent =
          `${title} added to your bag`;
      }

      if (variantElement) {
        variantElement.textContent =
          variantTitle || "";

        variantElement.hidden = !variantTitle;
      }

      if (imageElement instanceof HTMLImageElement) {
        if (image) {
          imageElement.src = image;
          imageElement.alt = title;
          imageElement.hidden = false;
        } else {
          imageElement.hidden = true;
        }
      }

      if (cartLink && cartUrl) {
        cartLink.href = cartUrl;
      }

      element.classList.add("is-visible");

      window.clearTimeout(timeoutId);

      timeoutId = window.setTimeout(
        hide,
        6500
      );
    }

    function hide() {
      window.clearTimeout(timeoutId);
      element?.classList.remove("is-visible");
    }

    return Object.freeze({
      show,
      hide
    });
  })();

  function initializePurchaseEngines(
    context = document
  ) {
    if (!window.HFDS) {
      return;
    }

    window.HFDS
      .queryAll(ROOT_SELECTOR, context)
      .forEach((root) => {
        if (!(root instanceof HTMLElement)) {
          return;
        }

        if (root.dataset.hfInitialized === "true") {
          return;
        }

        root.hfPurchaseEngine =
          new HFPurchaseEngine(root);
      });
  }

  window.HFDS?.ready(() => {
    initializePurchaseEngines(document);
  });

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initializePurchaseEngines(event.target);
    }
  );

  document.addEventListener(
    "shopify:section:unload",
    (event) => {
      window.HFDS
        ?.queryAll(ROOT_SELECTOR, event.target)
        .forEach((root) => {
          root.hfPurchaseEngine?.destroy();
        });
    }
  );

  window.HFDSPurchaseEngine = Object.freeze({
    version: "1.0.2",
    initialize: initializePurchaseEngines,
    cartConfirmation: HFCartConfirmation
  });
})();